import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../constants';

export default function TempAlertSettings({ motor, token, setMotor }) {
  // Default threshold assumption: 80°C if backend hasn't set one
  const [threshold, setThreshold] = useState(
    motor.tempAlertThreshold === undefined ? 80 : motor.tempAlertThreshold
  );
  const [enabled, setEnabled] = useState(
    motor.tempAlertEnabled === undefined ? true : !!motor.tempAlertEnabled
  );
  const [statusMsg, setStatusMsg] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setThreshold(motor.tempAlertThreshold ?? 80);
    setEnabled(motor.tempAlertEnabled === undefined ? true : !!motor.tempAlertEnabled);
  }, [motor]);

  const showTemporary = (msg) => {
    setStatusMsg(msg);
    setTimeout(() => setStatusMsg(''), 4000);
  };

  const saveThreshold = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/moteur/setTempAlertThreshold`,
        { id_moteur: motor._id, threshold: Number(threshold) },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        }
      );

      if (res.status === 200 && res.data) {
        setMotor(res.data);
        localStorage.setItem('motor', JSON.stringify(res.data));
        showTemporary('Seuil de température mis à jour.');
      } else if (res.status === 401) {
        alert('Session expirée, veuillez vous reconnecter.');
        localStorage.clear();
        window.location.href = '/login';
      } else {
        showTemporary('Erreur lors de la mise à jour.');
      }
    } catch (err) {
      console.error(err);
      showTemporary(err?.response?.data?.error || err.message || 'Erreur serveur');
    } finally {
      setLoading(false);
    }
  };

  const saveEnabled = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/moteur/setTempAlertEnabled`,
        { id_moteur: motor._id, enabled: !!enabled },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        }
      );

      if (res.status === 200 && res.data) {
        setMotor(res.data);
        localStorage.setItem('motor', JSON.stringify(res.data));
        showTemporary(`Alerte température ${enabled ? 'activée' : 'désactivée'}`);
      } else if (res.status === 401) {
        alert('Session expirée, veuillez vous reconnecter.');
        localStorage.clear();
        window.location.href = '/login';
      } else {
        showTemporary('Erreur lors de la mise à jour.');
      }
    } catch (err) {
      console.error(err);
      showTemporary(err?.response?.data?.error || err.message || 'Erreur serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6">
      <h4 className="border-solid border-4 border-b-0  border-r-0 border-l-0 border-bleuSTB pt-4 pb-4 text-xl 3xl:text-3xl underline">
        <div className="inline-flex font-bold underline mb-4 ">
          Paramètres d’alerte — température
        </div>
      </h4>
      {/* Card */}
      <div className="mt-2 mb-6 bg-white/40 backdrop-blur-xl p-5 rounded-2xl shadow-md border border-white/20 flex flex-col gap-6">

        {/* Top Row */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6">

          {/* Toggle + Text */}
          <div className="flex items-center justify-between md:justify-start gap-4 flex-1">
            <div
              className={`w-14 h-8 rounded-full flex items-center p-1 cursor-pointer transition-all 
                ${enabled ? 'bg-bleuSTB' : 'bg-gray-300'}`}
              onClick={(event) => { event.stopPropagation(); setEnabled((p) => !p); }}
              role="switch"
              aria-checked={enabled}
            >
              <div
                className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-all 
                  ${enabled ? 'translate-x-6' : ''}`}
              />
            </div>

            <div>
              <div className="text-base font-medium">
                Alerte {enabled ? 'activée' : 'désactivée'}
              </div>
              <div className="text-xs text-gray-600">
                Recevoir une alerte si la température dépasse
              </div>
            </div>
          </div>

          {/* Input minutes */}
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={-50}
              max={500}
              value={threshold}
              onChange={(e) => { e.stopPropagation(); setThreshold(Number(e.target.value)); }}
              className="border border-gray-300 focus:border-bleuSTB focus:ring-bleuSTB rounded-lg px-3 py-2 w-32 text-sm shadow-sm transition"
            />
            <span className="text-sm text-gray-700">°C</span>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex flex-wrap gap-3 justify-end">
          <button
            onClick={(e) => { e.stopPropagation(); saveEnabled(); }}
            className="px-4 py-2 rounded-lg text-sm border border-bleuSTB text-bleuSTB hover:bg-bleuSTB hover:text-white transition shadow-sm"
            disabled={loading}
          >
            Appliquer activation
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); saveThreshold(); }}
            className="px-4 py-2 rounded-lg text-sm bg-bleuSTB text-white hover:bg-opacity-90 transition shadow-sm"
            disabled={loading}
          >
            Enregistrer seuil
          </button>
        </div>

        {/* Status */}
        {statusMsg && (
          <div className="text-sm font-medium text-green-700">
            {statusMsg}
          </div>
        )}

        <div className="text-xs text-gray-600">
          Par défaut : 80 °C. Valeur acceptée : -50 — {500} °C.
        </div>
      </div>
    </div>
  );
}
